/**
 * Nutrient normalization utility to handle inconsistent nutrient code formats.
 * Maps display values, full names, and legacy formats to canonical nutrient codes.
 *
 * This ensures:
 * 1. PATCH payloads always contain valid backend-expected nutrient codes
 * 2. Backward compatibility with legacy/API-returned formats
 * 3. Consistent display across the app
 */

/**
 * Map of all possible nutrient input formats to their canonical codes
 */
const NUTRIENT_NORMALIZATION_MAP: Record<string, string> = {
  // Basic nutrients - already canonical
  N: "N",
  P: "P",
  K: "K",

  // Additional nutrients - canonical forms
  Cd: "Cd",
  Cr: "Cr",
  Pb: "Pb",
  Hg: "Hg",

  // Other nutrients - canonical short codes
  Al: "Al",
  B: "B",
  Ca: "Ca",
  Co: "Co",
  Cu: "Cu",
  Fe: "Fe",
  Mg: "Mg",
  Mn: "Mn",
  Mo: "Mo",
  Ni: "Ni",
  Se: "Se",
  Si: "Si",
  Na: "Na",
  S: "S",
  Zn: "Zn",

  // Display format mappings (e.g., "Zn | Zinc" -> "Zn")
  "Zn | Zinc": "Zn",
  "Zn|Zinc": "Zn",
  "S | Sulfur": "S",
  "S|Sulfur": "S",
  "Na | Sodium": "Na",
  "Na|Sodium": "Na",
  "Ni | Nickel": "Ni",
  "Ni|Nickel": "Ni",
  "Fe | Iron": "Fe",
  "Fe|Iron": "Fe",
  "Cu | Copper": "Cu",
  "Cu|Copper": "Cu",
  "Co | Cobalt": "Co",
  "Co|Cobalt": "Co",
  "B | Boron": "B",
  "B|Boron": "B",
  "Ca | Calcium": "Ca",
  "Ca|Calcium": "Ca",
  "Mg | Magnesium": "Mg",
  "Mg|Magnesium": "Mg",
  "Mn | Manganese": "Mn",
  "Mn|Manganese": "Mn",
  "Mo | Molybdenum": "Mo",
  "Mo|Molybdenum": "Mo",
  "Al | Aluminum": "Al",
  "Al|Aluminum": "Al",
  "Se | Selenium": "Se",
  "Se|Selenium": "Se",
  "Si | Silicon": "Si",
  "Si|Silicon": "Si",
  "Pb | Lead": "Pb",
  "Pb|Lead": "Pb",
  "Cr | Chromium": "Cr",
  "Cr|Chromium": "Cr",
  "Cd | Cadmium": "Cd",
  "Cd|Cadmium": "Cd",
  "Hg | Mercury": "Hg",
  "Hg|Mercury": "Hg",

  // Full name mappings (backend may return these)
  ZINC: "Zn",
  zinc: "Zn",
  SULFUR: "S",
  sulfur: "S",
  SODIUM: "Na",
  sodium: "Na",
  NICKEL: "Ni",
  nickel: "Ni",
  IRON: "Fe",
  iron: "Fe",
  COPPER: "Cu",
  copper: "Cu",
  COBALT: "Co",
  cobalt: "Co",
  BORON: "B",
  boron: "B",
  CALCIUM: "Ca",
  calcium: "Ca",
  MAGNESIUM: "Mg",
  magnesium: "Mg",
  MANGANESE: "Mn",
  manganese: "Mn",
  MOLYBDENUM: "Mo",
  molybdenum: "Mo",
  ALUMINUM: "Al",
  aluminum: "Al",
  SELENIUM: "Se",
  selenium: "Se",
  SILICON: "Si",
  silicon: "Si",
  NITROGEN: "N",
  nitrogen: "N",
  PHOSPHORUS: "P",
  phosphorus: "P",
  POTASSIUM: "K",
  potassium: "K",
  LEAD: "Pb",
  lead: "Pb",
  CHROMIUM: "Cr",
  chromium: "Cr",
  CADMIUM: "Cd",
  cadmium: "Cd",
  MERCURY: "Hg",
  mercury: "Hg",

  // P2O5 and K2O variants (common in backend)
  P2O5: "P",
  K2O: "K",
};

/**
 * Normalize a nutrient code to its canonical short form.
 * Handles display formats, full names, and legacy formats.
 *
 * @param nutrientInput - Raw nutrient code (e.g., "Zn | Zinc", "ZINC", "Zn")
 * @returns Canonical nutrient code (e.g., "Zn"), or empty string if not found
 *
 * @example
 * normalizeNutrientCode("Zn | Zinc") // returns "Zn"
 * normalizeNutrientCode("ZINC") // returns "Zn"
 * normalizeNutrientCode("Zn") // returns "Zn"
 * normalizeNutrientCode("unknown") // returns ""
 */
export const normalizeNutrientCode = (
  nutrientInput: string | undefined | null
): string => {
  if (!nutrientInput || typeof nutrientInput !== "string") {
    return "";
  }

  const trimmed = nutrientInput.trim();

  // Direct lookup (handles both canonical and mapped formats)
  if (NUTRIENT_NORMALIZATION_MAP[trimmed] !== undefined) {
    return NUTRIENT_NORMALIZATION_MAP[trimmed];
  }

  // If not found, return empty string (safer than returning input as-is)
  return "";
};

/**
 * Check if a nutrient code is valid (exists in normalization map).
 * Useful for validation before sending to backend.
 *
 * @param nutrientCode - Nutrient code to validate
 * @returns true if valid, false otherwise
 */
export const isValidNutrientCode = (
  nutrientCode: string | undefined | null
): boolean => {
  if (!nutrientCode) return false;
  const normalized = normalizeNutrientCode(nutrientCode);
  return normalized.length > 0;
};

/**
 * Get all valid canonical nutrient codes.
 * Useful for testing and validation.
 *
 * @returns Array of canonical nutrient codes
 */
export const getValidNutrientCodes = (): string[] => {
  return Array.from(new Set(Object.values(NUTRIENT_NORMALIZATION_MAP)));
};
